import {
    faArrowProgress,
    faCircleX,
    faFileArrowDown,
    faFileCsv,
    faFilePdf,
    faPrint,
    faReceipt,
    faTag,
    faList,
} from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { Order } from "../../../controlKitchenTypes"
import { DateTime } from "luxon"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"

import { printReceipt, saveReceiptAsFile, StandardTransactionResultResponse, TransactionResult } from "../productionApi"

import { useState, useEffect } from "react"
import { useApiMutation } from "../../../../../core/api/useApiMutation"

import { FilteredStatuses } from "../meetingService/meetingServiceTypes"
import { useExportOrdersToCsv } from "./ExportFunctionOrder"
import { MRT_RowSelectionState } from "material-react-table"
import { UseMutateAsyncFunction } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import StandardTransactionResultResponseModal from "./StandardTransactionResultResponseModal"
import { LoadingDialog } from "../../../../../shared/components/dialogs/LoadingDialog"
import LoadingLottie from "../assets/Loading-color-balls-jumping.json"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import {
    ActionBarItem,
    ActionBarMenu,
    ActionBarMenuItem,
    FloatingActionBar,
} from "../../../../../shared/components/table/FloatingActionBar"
import { ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { useLabelPrinter } from "../../../../serviceCenter/report/home/hooks/useLabelPrinter"
import { LabelPrinterExtensionDialog } from "../../../../../shared/components/dialogs/LabelPrinterExtensionDialog"
import { TakeawayLabelData } from "../../../../serviceCenter/report/external/brotherPrinter"
import { useDateTime } from "../../../../localization/useDateTime"

type FloatingOrderActionBarProps = {
    variant?: "default" | "pos"
    selectedRows: Order[]
    startDate: DateTime
    endDate: DateTime
    showPrintLabel?: boolean
    handleClearSelected: () => void
    setSelectedRows?: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>
    onStatusClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
    onPrintDeliveryList?: () => void
    hidePrintActions?: boolean
}

export default function FloatingOrderActionBar({
    variant = "default",
    showPrintLabel = true,
    selectedRows,
    startDate,
    endDate,
    handleClearSelected,
    onStatusClick,
    onPrintDeliveryList,
    hidePrintActions = false,
}: FloatingOrderActionBarProps) {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const isPos = variant === "pos"
    const isDefault = variant === "default"
    const orderIds = selectedRows ? selectedRows.map((order) => order.id) : []

    const { isInstalledError, clearState, printMultipleTakeawayLabels, hasError, printErrorMessage } = useLabelPrinter()

    // Show snackbar when bpac error occurs
    useEffect(() => {
        if (hasError && printErrorMessage) {
            enqueueSnackbar(printErrorMessage, { variant: "error" })
            clearState()
        }
    }, [hasError, printErrorMessage, clearState])

    const getOrderItems = (order: Order): string => {
        const orderLines = order.orderLines ?? []
        return orderLines.map((line) => `${line.items} x ${line.name}`).join(", ")
    }

    const handlePrintLabels = async () => {
        const labels: TakeawayLabelData[] = selectedRows.map((order) => ({
            orderId: order.id,
            customerName: order.customer?.displayName || "",
            deliveryDate: dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(order?.deliveryDetails?.time ?? order.deliveries?.[0]?.deliveryTime)),
            numberOfItems: order.lineNumber,
            description: getOrderItems(order),
        }))

        await printMultipleTakeawayLabels(labels)
    }

    const [isMultipleReceiptModal, setIsMultipleReceiptModal] = useState(false)

    const { mutateAsync: saveReceiptAsFileCall } = useApiMutation(saveReceiptAsFile, {})

    const handlePDFDownload = async () => {
        const response = await saveReceiptAsFileCall({ orderIds: orderIds })
        let filename = `Facilitynet-Receipt-${startDate}_${endDate}.pdf`
        const url = window.URL.createObjectURL(response)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", filename)
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(url)
        link.remove()
    }
    const { mutateAsync: callPrintReceipt } = useApiMutation(printReceipt, {
        async onSuccess(params, data, context) {
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("print-queued"),
            })
        },
    })

    const [orderStatusResponseResult, setOrderStatusResponseResult] = useState<StandardTransactionResultResponse>()
    const { mutateAsync: callRemovePrintReceiptfromQueue, isLoading } = useApiMutation(printReceipt, {
        async onSuccess(params, data, context) {
            setOrderStatusResponseResult(data)
        },
    })

    const exportSelectedOrders = useExportOrdersToCsv({ data: selectedRows, startDate, endDate })

    const [showConfirmModalForRemoveQue, setShowConfirmModalForRemoveQue] = useState(false)
    const [showConfirmModalForPrint, setShowConfirmModalForPrint] = useState(false)

    return (
        <>
            <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                {onStatusClick && (
                    <LocalizedStrict id="floating-action-button-order-status" attrs={{ title: true }}>
                        <ActionBarItem icon={faArrowProgress} title="Order status" onClick={onStatusClick} />
                    </LocalizedStrict>
                )}
                {!hidePrintActions && (
                    <LocalizedStrict id="floating-action-button-print" attrs={{ title: true }}>
                        <ActionBarMenu icon={faPrint} title="Print">
                            <LocalizedStrict
                                id={isPos ? "floating-action-button-print-receipt" : "floating-action-button-print"}
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faReceipt}
                                    title="Print"
                                    onClick={() => setIsMultipleReceiptModal(true)}
                                />
                            </LocalizedStrict>
                            {isPos && (
                                <>
                                   {showPrintLabel && <LocalizedStrict id="floating-action-button-print-label" attrs={{ title: true }}>
                                        <ActionBarMenuItem
                                            icon={faTag}
                                            title="Print label"
                                            onClick={() => handlePrintLabels()}
                                        />
                                    </LocalizedStrict>}
                                    {onPrintDeliveryList && (
                                        <LocalizedStrict
                                            id="meeting-service-table-floating-action-bar-delivery-list"
                                            attrs={{ title: true }}
                                        >
                                            <ActionBarMenuItem
                                                icon={faList}
                                                title="Delivery list"
                                                onClick={onPrintDeliveryList}
                                            />
                                        </LocalizedStrict>
                                    )}
                                </>
                            )}
                            {isDefault && (
                                <LocalizedStrict id="export-button-remove-print-title" attrs={{ title: true }}>
                                    <ActionBarMenuItem
                                        icon={faCircleX}
                                        title="Remove from printer queue"
                                        onClick={() => setShowConfirmModalForRemoveQue(true)}
                                    />
                                </LocalizedStrict>
                            )}
                        </ActionBarMenu>
                    </LocalizedStrict>
                )}

                <LocalizedStrict id="floating-action-button-export" attrs={{ title: true }}>
                    <ActionBarMenu icon={faFileArrowDown} title="Export">
                        <LocalizedStrict id="export-button-export-as-pdf-title" attrs={{ title: true }}>
                            <ActionBarMenuItem
                                icon={faFilePdf}
                                title="Save as PDF"
                                onClick={() => handlePDFDownload()}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict id="export-button-export-as-csv-title" attrs={{ title: true }}>
                            <ActionBarMenuItem
                                icon={faFileCsv}
                                title="Export as CSV"
                                onClick={() => exportSelectedOrders()}
                            />
                        </LocalizedStrict>
                    </ActionBarMenu>
                </LocalizedStrict>
            </FloatingActionBar>
            {isMultipleReceiptModal && (
                <ReceiptDialog
                    open={isMultipleReceiptModal}
                    onClose={() => setIsMultipleReceiptModal(false)}
                    receiptsForMultipleView={selectedRows}
                />
            )}
            {orderStatusResponseResult && (
                <StandardTransactionResultResponseModal
                    onClose={setOrderStatusResponseResult}
                    data={orderStatusResponseResult}
                />
            )}
            {showConfirmModalForRemoveQue && (
                <LocalizedStrict
                    id="production-orders-action-bar-confirm-queue-remove-dialog"
                    attrs={{ title: true, message: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        open={showConfirmModalForRemoveQue}
                        onClose={() => setShowConfirmModalForRemoveQue(false)}
                        title="Remove from printer queue"
                        message="Are you sure that you want to remove selected items from the printer queue?"
                        buttonLabelSubmit="Remove"
                        handleClick={() => callRemovePrintReceiptfromQueue({ orderIds: orderIds })}
                    />
                </LocalizedStrict>
            )}
            {showConfirmModalForPrint && (
                <LocalizedStrict
                    id="production-orders-action-bar-confirm-print-dialog"
                    attrs={{ title: true, message: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        title="Print receipts"
                        message="Are you sure you want to print the selected receipts?"
                        buttonLabelSubmit="Print"
                        open={showConfirmModalForPrint}
                        onClose={() => setShowConfirmModalForPrint(false)}
                        handleClick={() => callPrintReceipt({ orderIds: orderIds })}
                    />
                </LocalizedStrict>
            )}
            <LoadingDialog loading={isLoading} lottieAnim={LoadingLottie} />
            <LabelPrinterExtensionDialog open={isInstalledError} onClose={clearState} />
        </>
    )
}
